import React, {useCallback, useEffect, useMemo, useState} from "react"
import {Button, Checkbox, DatePicker, Descriptions, Drawer, Modal, Tag} from "antd"
import {Helmet} from "react-helmet-async"
import {useDispatch, useSelector} from "react-redux"
import {NavLink, useHistory, useParams} from "react-router-dom"
import {StateType} from "../../store/store"
import {BackButton} from "../../components/BackButton/BackButton"
import {Loading} from "../../components/Loading/Loading"
import moment from "moment/moment"
import {changePSLeakDeviceLocalLog, getPSLeakDeviceLocalLog, setPSLeakDeviceLocalLog, setWSPSLeakDeviceLocalLog} from "../../store/psLeakDeviceLocalLogReducer"
import * as yup from "yup"
import {useFormik} from "formik"
import {FormTextarea} from "../../components/Form/FormTextarea"
import {Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts"
import {psLeakDeviceIndicationApi} from "../../api/psLeakDeviceIndicationApi"
import {catchError} from "../../store/sliceHelper"
import locale from "antd/es/date-picker/locale/ru_RU"
import dayjs from "dayjs"
import {ColorForBarChart} from "../../helps/getColorForBarChart"
import {PSLeakDeviceIndicationsResponse} from "../../models/response"
import {PSLeakLocalLogTypeEnum, UserTypeEnum} from "../../types/types"
import {getTimeZoneDiffString} from "../../helps/timezoneDiff"

const schema = yup.object().shape({
    comment: yup.string().when("solved", {
        is: true,
        then: yup.string().min(20, "Минимальная длина 20 символов").required("Введите комментарий"),
    }),
})

export const PSLeakDeviceLocalLog = () =>
{
    const dispatch = useDispatch()
    const history = useHistory()
    const id = parseInt(useParams<{ id: string }>()?.id, 10)
    const profile = useSelector((state: StateType) => state.appReducer.profile)
    const log = useSelector((state: StateType) => state.psLeakDeviceLocalLogReducer.psLeakDeviceLocalLog)
    const ws = useSelector((state: StateType) => state.psLeakDeviceLocalLogReducer.ws)
    const controller = useMemo(() => new AbortController(), [id])
    const [graph, setGraph] = useState<PSLeakDeviceIndicationsResponse | null>(null)
    const [filter, setFilter] = useState<{ dateFrom?: string, dateTo?: string, idDevice?: number, name?: Array<string> }>({})
    const [showModal, setShowModal] = useState(false)
    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)

    useEffect(() =>
    {
        if (id) dispatch(getPSLeakDeviceLocalLog({id, controller}))

        return () =>
        {
            controller.abort()
            dispatch(setPSLeakDeviceLocalLog(null))
        }
    }, [dispatch, id, controller])

    useEffect(() =>
    {
        if (ws && ws.id === id)
        {
            dispatch(getPSLeakDeviceLocalLog({id, controller, withoutLoadingIndicator: true}))
        }
        dispatch(setWSPSLeakDeviceLocalLog(null))
    }, [dispatch, ws, id, controller])

    useEffect(() =>
    {
        if (log)
        {
            const dateFrom = new Date(log.date)
            dateFrom.setUTCHours(dateFrom.getUTCHours() - 1)
            const newDateFrom = dateFrom.toISOString()

            const dateTo = new Date(log.date)
            dateTo.setUTCHours(dateTo.getUTCHours() + 1)
            const newDateTo = dateTo.toISOString()

            let name: string[] = []

            switch (log.idLogType)
            {
                case PSLeakLocalLogTypeEnum.Leak:
                    name = ["ХВС", "ГВС"]
                    break
                case PSLeakLocalLogTypeEnum.NoDataHVS:
                case PSLeakLocalLogTypeEnum.LeakHVS:
                    name = ["ХВС"]
                    break
                case PSLeakLocalLogTypeEnum.NoDataGVS:
                case PSLeakLocalLogTypeEnum.LeakGVS:
                    name = ["ГВС"]
                    break
                case PSLeakLocalLogTypeEnum.NoDataVKT:
                case PSLeakLocalLogTypeEnum.LeakVKT:
                    name = ["ВКТ"]
                    break
            }

            setFilter({idDevice: log.idDevice, name: name, dateFrom: newDateFrom, dateTo: newDateTo})
        }
    }, [log])

    const showDrawer = () =>
    {
        setOpen(true)
        if (typeof window !== "undefined" && window.document)
        {
            document.body.style.overflowY = "hidden"
        }
    }

    const onClose = () =>
    {
        setOpen(false)
        document.body.style.overflowY = "unset"
    }

    const formik = useFormik({
        initialValues: {
            comment: log?.comment || "",
            solved: log?.solved === null ? null : true,
        },
        validationSchema: schema,
        enableReinitialize: true,
        onSubmit: async (values) =>
        {
            if (log)
            {
                try
                {
                    setLoading(true)
                    const {payload}: any = await dispatch(changePSLeakDeviceLocalLog({
                        id: log.id,
                        comment: values.comment,
                        solved: values.solved,
                        controller,
                    }))
                    if (payload) setShowModal(false)
                } finally
                {
                    setLoading(false)
                }
            }
        },
    })
    const {values, handleChange, handleBlur, handleSubmit, errors, touched, setFieldValue, submitForm, resetForm} = formik

    const getGraph = useCallback(async () =>
    {
        if (log && filter.idDevice && filter.name && open)
        {
            try
            {
                setGraph(null)

                const res = await psLeakDeviceIndicationApi.getIndicationGraph({
                    idDevice: filter.idDevice, name: filter.name, dateFrom: filter.dateFrom, dateTo: filter.dateTo, controller,
                })

                if (res.data) setGraph(res.data)
            } catch (e: any)
            {
                catchError(e, dispatch)
            }

        }
    }, [dispatch, log, open, filter.idDevice, filter.name, filter.dateFrom, filter.dateTo, controller])

    useEffect(() =>
    {
        getGraph()
    }, [getGraph])

    const graphMap = useMemo(() =>
    {
        const bars: Array<string> = []
        const datas: Array<number> = []
        const data: Array<any> = []

        graph?.data?.forEach(i =>
        {
            if (!bars.includes(i.name)) bars.push(i.name)
            datas.push(new Date(i.date).setUTCSeconds(0))
        })

        datas.forEach(i =>
        {
            const item: any = {date: `${moment(i).format("DD.MM.YYYY HH:mm")} ${getTimeZoneDiffString(log?.timeZone)}`}
            const indications = graph?.data?.filter(f => new Date(f.date).setUTCSeconds(0) === i)

            indications?.forEach(f => item[f.name] = f.value)

            data.push(item)
        })


        if (graph?.total === 0) return <div style={{textAlign: "center", margin: 20, fontSize: 16}}>Нет данных</div>

        ColorForBarChart.Refresh()

        return <div style={{height: 500}}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart width={1000} height={500} data={data}>
                    <CartesianGrid strokeDasharray="3 3"/>
                    <XAxis dataKey="date"/>
                    <YAxis/>
                    <Tooltip/>
                    <Legend/>
                    {bars.map(i => <Bar key={i} dataKey={i} fill={ColorForBarChart.GetColor()}/>)}
                </BarChart>
            </ResponsiveContainer>
        </div>
    }, [graph])

    if (!log) return <Loading/>

    return (
        <div className="description-page">
            <Helmet>
                <title>{`Лог ${log?.id}`}</title>
            </Helmet>
            <div className="description-header">
                <BackButton onClick={() => history.goBack()}/>
                <div>
                    <Button type="link" onClick={() => setShowModal(true)}>{typeof log?.solved === "boolean" &&
                    ((profile?.idUserType !== UserTypeEnum.OPERATOR_COMPANY && profile?.idUserType !== UserTypeEnum.BUH_COMPANY) || (log.idLogType < 1 || log.idLogType > 10))
                        ? "Отметить решенной" : "Добавить комментарий"}</Button>
                </div>
            </div>

            <Descriptions bordered column={{xs: 1, sm: 1, md: 1, lg: 2}}>
                <Descriptions.Item label="Тип">{log?.nameLogType}</Descriptions.Item>
                <Descriptions.Item label="ID">{log?.id}</Descriptions.Item>
                <Descriptions.Item label="ID локальный">{log?.idLocal}</Descriptions.Item>
                <Descriptions.Item label="Оборудование">
                    <NavLink to={`/psLeakDevice/${log.idDevice}`}>{log.nameDevice}</NavLink>
                </Descriptions.Item>
                <Descriptions.Item label="Объект">
                    <NavLink to={`/object/${log.idObject}`}>{log.nameObject}</NavLink>
                </Descriptions.Item>
                <Descriptions.Item label="Группа">
                    <NavLink to={`/objectGroup/${log.idObjectGroup}`}>{log.nameObjectGroup}</NavLink>
                </Descriptions.Item>
                <Descriptions.Item label="Компания">
                    <NavLink to={`/company/${log.idCompany}`}>{log.nameCompany}</NavLink>
                </Descriptions.Item>
                <Descriptions.Item label="Дата">{`${moment(log.date).format("DD.MM.YYYY HH:mm")} ${getTimeZoneDiffString(log.timeZone)}`}</Descriptions.Item>
                <Descriptions.Item label="Описание">{log?.description}</Descriptions.Item>
                <Descriptions.Item label="Статус">
                    {
                        typeof log.solved === "boolean"
                            ? <Tag color={log.solved ? "green" : "volcano"} style={{marginRight: 0}}>{log.solved ? "Решено" : "Не решено"}</Tag>
                            : <Tag color="default" style={{marginRight: 0}}>Системное</Tag>
                    }
                </Descriptions.Item>
                <Descriptions.Item label="Комментарий">{log?.comment}</Descriptions.Item>
                {log?.cancel ? <Descriptions.Item label="Сервисное сообщение">{log?.cancel}</Descriptions.Item> : null}
            </Descriptions>

            {filter.idDevice ? <div style={{marginTop: 20}}><Button onClick={showDrawer}>Посмотреть показания</Button></div> : null}

            <Modal title={typeof log?.solved === "boolean" ? "Отметить решенной" : "Добавить комментарий"}
                   centered
                   open={showModal}
                   onOk={submitForm}
                   onCancel={() =>
                   {
                       setShowModal(false)
                       resetForm()
                   }}
                   okText="Сохранить"
                   okButtonProps={{loading}}
            >
                <FormTextarea placeholder="Комментарий"
                              value={values.comment}
                              name="comment"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              isInvalid={touched.comment && errors.comment}
                              allowClear
                              autoSize={{minRows: 2}}
                />
                {
                    typeof values.solved === "boolean" && (log.idLogType < 1 || log.idLogType > 10)
                        ? <div style={{marginTop: 20}}>
                            <Checkbox checked={values.solved}
                                      name="solved"
                                      onChange={handleChange}
                            >
                                <Tag color={values.solved ? "green" : "default"}>Решено</Tag>
                            </Checkbox>
                        </div>
                        : null
                }
            </Modal>

            <Drawer title="График"
                    placement="bottom"
                    height={600}
                    onClose={onClose}
                    open={open}
                    bodyStyle={{padding: 10}}
                    extra={<div className="table-title-extra">
                        <div style={{display: "flex", alignItems: "center", gap: 10}}>
                            <div>Начало периода</div>
                            <DatePicker locale={locale}
                                        showTime
                                        format="DD.MM.YYYY HH:mm"
                                        showSecond={false}
                                        value={filter.dateFrom ? dayjs(filter.dateFrom) : null}
                                        onChange={(value) => setFilter({...filter, dateFrom: value ? value.toJSON() : undefined})}
                                        disabledDate={(currentDate) => currentDate > dayjs(new Date())}
                            />
                        </div>
                        <div style={{display: "flex", alignItems: "center", gap: 10}}>
                            <div>Конец периода</div>
                            <DatePicker locale={locale}
                                        showTime
                                        format="DD.MM.YYYY HH:mm"
                                        showSecond={false}
                                        value={filter.dateTo ? dayjs(filter.dateTo) : null}
                                        onChange={(value) => setFilter({...filter, dateTo: value ? value.toJSON() : undefined})}
                                        disabledDate={(currentDate) => filter.dateFrom ? currentDate < dayjs(filter.dateFrom) || currentDate > dayjs(new Date()) : currentDate > dayjs(new Date())}
                            />
                        </div>
                    </div>}
            >
                {graph ? graphMap : <Loading/>}
            </Drawer>
        </div>
    )
}